import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useSelection } from '../context/SelectionContext';

export function useUnviewedSuggestions(studentId) {
  const [hasUnviewed, setHasUnviewed] = useState(false);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [countsBySemester, setCountsBySemester] = useState({});
  const [loading, setLoading] = useState(false);

  const { unviewedCountVersion, triggerUnviewedCountRefetch } = useSelection();

  const checkUnviewed = useCallback(async () => {
    if (!studentId) {
      setHasUnviewed(false);
      setUnviewedCount(0);
      setCountsBySemester({});
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/parent/children/${studentId}/unviewed-suggested-courses`);
      setHasUnviewed(response.data.has_unviewed);
      setUnviewedCount(response.data.unviewed_count);
      setCountsBySemester(response.data.counts_by_semester || {});
    } catch (err) {
      console.error('Error checking unviewed suggestions:', err);
      setHasUnviewed(false);
      setUnviewedCount(0);
      setCountsBySemester({});
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  // Refetch when studentId changes or when unviewedCountVersion changes (triggered by markAsViewed)
  useEffect(() => {
    checkUnviewed();
  }, [checkUnviewed, unviewedCountVersion]);

  const markAsViewed = useCallback(async (emailId) => {
    if (!studentId || !emailId) return;

    try {
      await api.post(`/parent/children/${studentId}/suggested-courses/emails/${emailId}/mark-viewed`);
      // Trigger refetch across all components using this hook
      triggerUnviewedCountRefetch();
    } catch (err) {
      console.error('Error marking email as viewed:', err);
    }
  }, [studentId, triggerUnviewedCountRefetch]);

  const dismissPopup = useCallback(() => {
    setHasUnviewed(false);
  }, []);

  return {
    hasUnviewed,
    unviewedCount,
    countsBySemester,
    loading,
    markAsViewed,
    dismissPopup,
    refetch: checkUnviewed,
  };
}
